import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { iconBackgroundStyle } from '@/utils/iconBackground';

interface TipPayload {
    name: string;
    price: number;
    rarity: string;
    extra?: string;
    image?: string;
    iconBackground?: string;
}

interface Position {
    x: number;
    y: number;
    placement: 'above' | 'below';
    arrowX: number;
}

const W = 212;
const H = 244;

const CoinIcon = ({ size = 15 }: { size?: number }) => (
    <span
        className="rounded-full grid place-items-center shrink-0"
        style={{
            width: size,
            height: size,
            background: 'radial-gradient(circle at 35% 30%, #ffe08a, #ffc24d)',
            boxShadow: '0 0 10px rgba(255,194,77,0.7)',
        }}
    >
        <span
            className="font-black leading-none"
            style={{ fontSize: size * 0.6, color: '#6a4500', textShadow: '0 1px 0 rgba(255,255,255,0.45)' }}
        >
            $
        </span>
    </span>
);

const GlobalTooltip = () => {
    const [mounted, setMounted] = useState(false);
    const [show, setShow] = useState(false);
    const [data, setData] = useState<TipPayload | null>(null);
    const [pos, setPos] = useState<Position>({ x: 0, y: 0, placement: 'above', arrowX: W / 2 });
    const targetRef = useRef<Element | null>(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        function readTarget(el: HTMLElement): TipPayload | null {
            const name = el.dataset.name;
            const price = el.dataset.price;
            const rarity = el.dataset.rarity;
            if (!name || !price || !rarity) return null;
            return {
                name,
                price: Number(price),
                rarity,
                extra: el.dataset.extra,
                image: el.dataset.image,
                iconBackground: el.dataset.iconBackground,
            };
        }

        function place(el: Element): Position {
            const r = el.getBoundingClientRect();
            let x = r.left + r.width / 2 - W / 2;
            let y = r.top - H - 12;
            let placement: Position['placement'] = 'above';
            if (y < 8) {
                y = r.bottom + 12;
                placement = 'below';
            }
            x = Math.max(8, Math.min(window.innerWidth - W - 8, x));
            const arrowX = r.left + r.width / 2 - x;
            return { x, y, placement, arrowX: Math.max(14, Math.min(W - 14, arrowX)) };
        }

        function hide() {
            if (!targetRef.current) return;
            targetRef.current = null;
            setShow(false);
        }

        // Drive show/hide off `mouseover` rather than `mouseout`: it fires for
        // every element the pointer enters, so moving onto a non-tip element
        // (background, a button, a modal) reliably hides the tip. `mouseout`
        // is unreliable here because the live-updating jackpot/coinflip tiles
        // are frequently re-rendered or removed while hovered — when the node
        // under the cursor is unmounted, no `mouseout` ever fires and the tip
        // would stay stuck on screen.
        function onMove(e: Event) {
            const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-tip="1"]');
            if (!target) {
                hide();
                return;
            }
            if (target === targetRef.current) return;
            const payload = readTarget(target);
            if (!payload) {
                hide();
                return;
            }
            targetRef.current = target;
            setData(payload);
            setPos(place(target));
            setShow(true);
        }
        function onScroll() {
            if (targetRef.current) setPos(place(targetRef.current));
        }

        // Any click (e.g. opening a modal) dismisses the tip immediately.
        function onDown() {
            hide();
        }

        // Watchdog: if the hovered tile is removed from the DOM (round update,
        // wheel/strip animation, column re-fit), there is no `mouseout`, so
        // hide once its node is no longer connected.
        const mo = new MutationObserver(() => {
            if (targetRef.current && !targetRef.current.isConnected) hide();
        });
        mo.observe(document.body, { childList: true, subtree: true });

        document.addEventListener('mouseover', onMove, true);
        document.addEventListener('mousedown', onDown, true);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onScroll);

        return () => {
            mo.disconnect();
            document.removeEventListener('mouseover', onMove, true);
            document.removeEventListener('mousedown', onDown, true);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onScroll);
        };
    }, []);

    if (!mounted) return null;

    const rarity = data?.rarity || '#3ad6ff';

    return createPortal(
        <div
            className={`g-tip ${show ? 'show' : ''} ${pos.placement}`}
            style={
                {
                    left: pos.x,
                    top: pos.y,
                    width: W,
                    '--arrow-x': `${pos.arrowX}px`,
                    '--tip-rarity': rarity,
                    borderColor: `color-mix(in srgb, ${rarity} 55%, transparent)`,
                    boxShadow: `0 0 26px -6px ${rarity}, 0 18px 44px -18px rgba(0,0,0,0.9)`,
                } as React.CSSProperties
            }
        >
            {data && (
                <>
                    {/* image panel — rarity-tinted, matching the skin tiles */}
                    <div
                        className="relative grid place-items-center rounded-[11px] overflow-hidden mb-2.5"
                        style={{
                            height: 122,
                            background: `radial-gradient(circle at 50% 32%, color-mix(in srgb, ${rarity} 24%, transparent), rgba(0,0,0,0.34))`,
                            border: `1px solid color-mix(in srgb, ${rarity} 38%, transparent)`,
                            boxShadow: `inset 0 0 22px -6px ${rarity}`,
                        }}
                    >
                        <span
                            className="absolute top-2 left-2 w-1.5 h-1.5 rounded-full"
                            style={{ background: rarity, boxShadow: `0 0 6px ${rarity}` }}
                        />
                        {data.image && (
                            <img
                                src={data.image}
                                alt=""
                                className="w-[80%] h-[80%] object-contain"
                                style={{
                                    filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.5))',
                                    ...iconBackgroundStyle(data.iconBackground),
                                }}
                            />
                        )}
                    </div>

                    {/* price */}
                    <div className="flex items-center gap-1.5 font-extrabold text-[16px] text-accent_5 tabular-nums">
                        <CoinIcon size={15} />
                        {data.price.toFixed(2)}
                    </div>

                    {/* name with a rarity marker */}
                    <div className="flex items-start gap-1.5 mt-1.5">
                        <span
                            className="w-1.5 h-1.5 rounded-full shrink-0 mt-[5px]"
                            style={{ background: rarity, boxShadow: `0 0 6px ${rarity}` }}
                        />
                        <span className="text-text_2 text-[12.5px] font-medium leading-snug">
                            {data.name}
                        </span>
                    </div>
                </>
            )}
        </div>,
        document.body
    );
};

export default GlobalTooltip;
